import type { IAdminDeviceControlConfig } from '@/modules/admin/entities/device-controller.d';
import { getRuntimeConfig } from '@/runtime/window-globals';

const OPENIM_API_ADDR = 'https://openim.qiyunedu.com/api';
const OPENIM_WS_ADDR = 'wss://openim.qiyunedu.com/msg_gateway';

export interface TenantWebEnv {
  moduleVersionsUrl?: string;
  tenantID?: string;
  realm: string;
  appId: string;
  apiBaseURL: string;
  remoteI18n: boolean;
  showLanguageSwitcher: boolean;
  mode: string;
  miniBaseURL?: string;
  deviceControl?: IAdminDeviceControlConfig;
  workspaceNotice: {
    apiAddr: string;
    externalOrigins: string[];
    wsAddr: string;
  };
}

/** firstDefined 按运行时配置优先级返回第一个显式配置值。 */
function firstDefined<T>(...values: Array<T | undefined>): T | undefined {
  return values.find((value): value is T => value !== undefined);
}

/** parseBoolean 把 html runtime 或环境变量中的布尔开关统一成真实布尔值。 */
function parseBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  return undefined;
}

/** parseOriginList 把运行时配置或逗号分隔环境变量收敛为去重的 HTTPS Origin 列表。 */
function parseOriginList(value: unknown): string[] {
  let entries: unknown[] = [];
  if (Array.isArray(value)) entries = value;
  if (typeof value === 'string') entries = value.split(',');
  return [...new Set(entries.flatMap((entry) => {
    if (typeof entry !== 'string') return [];
    try {
      const url = new URL(entry.trim());
      return url.protocol === 'https:' && url.origin === url.href.replace(/\/$/, '')
        ? [url.origin]
        : [];
    } catch {
      return [];
    }
  }))];
}

/** normalizeMode 归一化不同来源的运行环境名称，供登录和小程序地址策略共用。 */
function normalizeMode(value?: string): string | undefined {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === 'development') return 'local';
  if (normalized === 'production') return 'prod';
  return normalized;
}

/** getWorkspaceNoticeConfig 固定通知中心连接集群，并读取业务外链白名单。 */
function getWorkspaceNoticeConfig(
  runtime: ReturnType<typeof getRuntimeConfig>,
): TenantWebEnv['workspaceNotice'] {
  return {
    // 员工 IM token 与旧仓库固定的 OpenIM 集群配套，不能被部署环境变量指向其他实例。
    apiAddr: OPENIM_API_ADDR,
    externalOrigins: parseOriginList(firstDefined(
      runtime?.workspaceNotice?.externalOrigins,
      import.meta.env.VITE_WORKSPACE_NOTICE_EXTERNAL_ORIGINS,
    )),
    wsAddr: OPENIM_WS_ADDR,
  };
}

/** getEnv 汇总 html runtime 与构建环境变量，输出 tenant-web 运行时配置。 */
export function getEnv(): TenantWebEnv {
  const runtime = getRuntimeConfig();
  const buildMode = firstDefined(
    normalizeMode(import.meta.env.VITE_APP_MODE),
    normalizeMode(import.meta.env.VITE_APP_ENV),
  );
  const mode = firstDefined(
    normalizeMode(runtime?.mode),
    buildMode,
    normalizeMode(import.meta.env.MODE),
  ) ?? 'local';
  return {
    moduleVersionsUrl: firstDefined(runtime?.moduleVersionsUrl, import.meta.env.VITE_MODULE_VERSIONS_URL),
    tenantID: firstDefined(runtime?.tenantID, import.meta.env.VITE_TENANT_ID) ?? 'playground-tenant',
    realm: firstDefined(runtime?.realm, import.meta.env.VITE_REALM)?.trim() || 'default',
    appId: firstDefined(runtime?.appId, import.meta.env.VITE_APP_BUNDLE_ID) ?? 'com.huashicloud.tenant.web',
    apiBaseURL: firstDefined(runtime?.apiBaseURL, import.meta.env.VITE_API_BASE_URL) ?? '/api',
    remoteI18n: firstDefined(runtime?.remoteI18n, import.meta.env.VITE_REMOTE_I18N === 'true') ?? false,
    showLanguageSwitcher: firstDefined(
      runtime?.showLanguageSwitcher,
      parseBoolean(import.meta.env.VITE_SHOW_LANGUAGE_SWITCHER),
    ) ?? true,
    mode,
    miniBaseURL: firstDefined(runtime?.miniBaseURL, import.meta.env.VITE_MINI_BASE_URL),
    deviceControl: runtime?.deviceControl,
    workspaceNotice: getWorkspaceNoticeConfig(runtime),
  };
}
